import { useEffect, useState } from "react";
import { descargarArchivo } from "../fileDownload";

/**
 * EstadoCuentaTab
 * Pestaña "Estado de cuenta" dentro del módulo Reportes.
 * Usa exactamente los mismos tokens de estilo que el resto de Reportes.tsx
 * (cardStyle, statLabel, statValue, input, colores y tipografías).
 *
 * Backend: app/routes/estado_cuenta.py (ya instalado y probado — responde
 * 401 sin token, lo cual confirma que está bien registrado).
 */

const API = "/api";
const token = () => localStorage.getItem("token");

const COLONES = (n: number) => `₡${Math.round(n).toLocaleString()}`;
const FECHA = (iso: string) => new Date(iso).toLocaleDateString("es-CR", { day: "2-digit", month: "short", year: "numeric" });

const METODO_LABEL: Record<string, string> = {
  efectivo: "Efectivo",
  sinpe: "SINPE Móvil",
  transferencia: "Transferencia",
  tarjeta: "Tarjeta",
};

type CreditoResumen = {
  venta_credito_id: number;
  fecha: string;
  saldo_inicial: number;
  saldo_pendiente: number;
  estado: string;
  al_dia: boolean;
};

type ClienteResultado = {
  cliente_id: number;
  nombre: string;
  cedula: string;
  telefono: string;
  creditos: CreditoResumen[];
};

type Abono = {
  id: number;
  fecha: string;
  monto: number;
  metodo: string;
  saldo_despues: number;
};

type EstadoCuentaDetalle = {
  cliente: { nombre: string; cedula: string; telefono: string };
  venta: { id: number; fecha: string; saldo_inicial: number; saldo_pendiente: number; estado: string; proxima_fecha_pago: string | null };
  abonos: Abono[];
};

// Mismos tokens visuales que el resto de Reportes.tsx
const cardStyle = { background: "#fff", borderRadius: "16px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" };
const statLabel = { fontSize: "12px", fontWeight: 700 as const, color: "#6b7280", marginBottom: "6px" };
const statValue = { fontFamily: "Montserrat,sans-serif", fontWeight: 900 as const, fontSize: "24px", color: "#1a1a2e" };
const inputStyle = { padding: "8px 10px", borderRadius: "8px", border: "1.5px solid #e5e7eb", fontSize: "13px", fontFamily: "Nunito,sans-serif" };
const btnPrimary = { background: "#0D47A1", color: "#fff", border: "none", borderRadius: "8px", padding: "9px 18px", fontWeight: 700 as const, fontSize: "13px", cursor: "pointer", fontFamily: "Nunito,sans-serif" };
const btnGhost = { background: "#fff", color: "#6b7280", border: "1.5px solid #e5e7eb", borderRadius: "8px", padding: "9px 18px", fontWeight: 700 as const, fontSize: "13px", cursor: "pointer", fontFamily: "Nunito,sans-serif" };

export default function EstadoCuentaTab() {
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState<ClienteResultado[]>([]);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [clienteAbierto, setClienteAbierto] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [detalle, setDetalle] = useState<EstadoCuentaDetalle | null>(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setCargandoLista(true);
    setError(null);
    const params = new URLSearchParams();
    if (query.trim()) params.append("q", query.trim());
    const timer = setTimeout(() => {
      fetch(`${API}/reportes/estado-cuenta/clientes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token()}` },
      })
        .then((r) => {
          if (!r.ok) throw new Error();
          return r.json();
        })
        .then((d) => !cancelado && Array.isArray(d) && setResultados(d))
        .catch(() => !cancelado && setError("No se pudo cargar la lista de clientes."))
        .finally(() => !cancelado && setCargandoLista(false));
    }, 350);
    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [query]);

  const abrirCredito = (ventaCreditoId: number) => {
    setCargandoDetalle(true);
    setError(null);
    fetch(`${API}/reportes/estado-cuenta/${ventaCreditoId}`, {
      headers: { Authorization: `Bearer ${token()}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(setDetalle)
      .catch(() => setError("No se pudo cargar el estado de cuenta de este crédito."))
      .finally(() => setCargandoDetalle(false));
  };

  const [descargandoPDF, setDescargandoPDF] = useState(false);

  // PDF con todas las compras a crédito de un cliente (hoja de estado de cuenta)
  const [descargandoCliente, setDescargandoCliente] = useState<number | null>(null);

  const descargarPDFCliente = async (c: ClienteResultado) => {
    setDescargandoCliente(c.cliente_id);
    const limpio = (c.nombre || "cliente").replace(/[^A-Za-z0-9ÁÉÍÓÚÜÑáéíóúüñ]+/g, "_");
    const nombre = `estado_cuenta_${limpio}_${new Date().toISOString().slice(0, 10)}.pdf`;
    try {
      await descargarArchivo(`${API}/reportes/estado-cuenta/cliente/${c.cliente_id}/pdf`, nombre);
    } finally {
      setDescargandoCliente(null);
    }
  };

  const [descargandoComprobante, setDescargandoComprobante] = useState<number | null>(null);

  const descargarComprobanteSaldo = async (c: ClienteResultado) => {
    setDescargandoComprobante(c.cliente_id);
    const limpio = (c.nombre || "cliente").replace(/[^A-Za-z0-9ÁÉÍÓÚÜÑáéíóúüñ]+/g, "_");
    const nombre = `comprobante_saldo_${limpio}_${new Date().toISOString().slice(0, 10)}.pdf`;
    try {
      await descargarArchivo(`${API}/reportes/estado-cuenta/cliente/${c.cliente_id}/comprobante-saldo/pdf`, nombre);
    } finally {
      setDescargandoComprobante(null);
    }
  };

  const descargarPDF = async () => {
    setDescargandoPDF(true);
    const nombre = `estado_cuenta_${detalle?.cliente.nombre.replace(/\s+/g, "_")}_${detalle?.venta.id}.pdf`;
    await descargarArchivo(`${API}/reportes/estado-cuenta/${detalle?.venta.id}/pdf`, nombre);
    setDescargandoPDF(false);
  };

  // ---------- Vista de detalle (lo que se envía / imprime para el cliente) ----------
  if (detalle) {
    const { cliente, venta, abonos } = detalle;
    const totalAbonado = abonos.reduce((s, a) => s + a.monto, 0);
    const porcentaje = venta.saldo_inicial > 0 ? Math.min(100, Math.round((totalAbonado / venta.saldo_inicial) * 100)) : 0;
    const alDia = venta.saldo_pendiente <= 0;

    const telLimpio = (cliente.telefono || "").replace(/\D/g, "");
    const mensajeWhatsapp = encodeURIComponent(
      `Hola ${cliente.nombre}, le compartimos su estado de cuenta:\n` +
        `Saldo inicial: ${COLONES(venta.saldo_inicial)}\n` +
        `Total abonado: ${COLONES(totalAbonado)}\n` +
        `Saldo pendiente: ${COLONES(venta.saldo_pendiente)}\n` +
        (alDia ? "¡Está al día, gracias por su pago!" : "Cualquier consulta, con gusto le ayudamos.")
    );

    return (
      <div>
        <button onClick={() => setDetalle(null)} style={{ ...btnGhost, marginBottom: "16px" }}>
          ← Volver a la búsqueda
        </button>

        {/* Datos del cliente */}
        <div style={{ ...cardStyle, marginBottom: "16px", display: "flex", flexWrap: "wrap", gap: "24px" }}>
          <div>
            <div style={statLabel}>Cliente</div>
            <div style={{ fontWeight: 800, fontSize: "16px" }}>{cliente.nombre}</div>
          </div>
          <div>
            <div style={statLabel}>Cédula</div>
            <div style={{ fontWeight: 700 }}>{cliente.cedula}</div>
          </div>
          <div>
            <div style={statLabel}>Teléfono</div>
            <div style={{ fontWeight: 700 }}>{cliente.telefono || "-"}</div>
          </div>
          <div>
            <div style={statLabel}>Compra #</div>
            <div style={{ fontWeight: 700 }}>{venta.id} · {FECHA(venta.fecha)}</div>
          </div>
        </div>

        {/* Progreso de pago */}
        <div style={{ ...cardStyle, marginBottom: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <div style={{ fontWeight: 800, fontSize: "14px" }}>Progreso de pago</div>
            <span
              style={{
                fontSize: "12px",
                fontWeight: 700,
                padding: "4px 12px",
                borderRadius: "999px",
                background: alDia ? "#ECFDF5" : "#FEF3C7",
                color: alDia ? "#065F46" : "#B45309",
              }}
            >
              {alDia ? "✓ Al día" : "En curso"}
            </span>
          </div>
          <div style={{ display: "flex", height: "28px", borderRadius: "10px", overflow: "hidden", marginBottom: "8px" }}>
            <div
              style={{
                width: `${Math.max(porcentaje, 8)}%`,
                background: "#22c55e",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: "11px",
                fontWeight: 700,
              }}
            >
              {porcentaje}%
            </div>
            <div style={{ flex: 1, background: "#F3F4F6" }} />
          </div>
          <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
            <span><span style={{ display: "inline-block", width: "10px", height: "10px", background: "#22c55e", borderRadius: "3px", marginRight: "4px" }} />Pagado</span>
            <span><span style={{ display: "inline-block", width: "10px", height: "10px", background: "#F3F4F6", borderRadius: "3px", marginRight: "4px" }} />Pendiente</span>
          </div>
        </div>

        {/* Los 3 números clave */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "14px", marginBottom: "16px" }}>
          <div style={cardStyle}>
            <div style={statLabel}>Saldo inicial</div>
            <div style={statValue}>{COLONES(venta.saldo_inicial)}</div>
          </div>
          <div style={cardStyle}>
            <div style={statLabel}>Total abonado</div>
            <div style={{ ...statValue, color: "#22c55e" }}>{COLONES(totalAbonado)}</div>
          </div>
          <div style={{ ...cardStyle, border: "2px solid #0D47A1" }}>
            <div style={{ ...statLabel, color: "#0D47A1" }}>Saldo pendiente</div>
            <div style={{ ...statValue, color: "#0D47A1" }}>{COLONES(venta.saldo_pendiente)}</div>
          </div>
        </div>

        {/* Explicación simple, pensada para copiar/enviar */}
        <div style={{ background: "#EFF6FF", borderRadius: "16px", padding: "16px 20px", marginBottom: "16px", fontSize: "14px", color: "#1e3a8a", lineHeight: 1.5 }}>
          {alDia ? (
            <>¡Felicidades! {cliente.nombre.split(" ")[0]} ya canceló por completo el saldo de <strong>{COLONES(venta.saldo_inicial)}</strong> correspondiente a su compra.</>
          ) : (
            <>
              De su compra de <strong>{COLONES(venta.saldo_inicial)}</strong>, ha abonado <strong>{COLONES(totalAbonado)}</strong> en{" "}
              {abonos.length} {abonos.length === 1 ? "pago" : "pagos"}. Le queda un saldo pendiente de{" "}
              <strong>{COLONES(venta.saldo_pendiente)}</strong>
              {venta.proxima_fecha_pago && <> · próximo pago sugerido: {FECHA(venta.proxima_fecha_pago)}</>}.
            </>
          )}
        </div>

        {/* Historial de abonos */}
        <div style={{ ...cardStyle, padding: 0, overflow: "hidden", marginBottom: "16px" }}>
          <div style={{ padding: "16px 20px", fontWeight: 800, fontSize: "14px", borderBottom: "1px solid #f3f4f6" }}>
            Historial de abonos ({abonos.length})
          </div>
          {abonos.map((a, i) => (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 20px", borderTop: i > 0 ? "1px solid #f3f4f6" : undefined }}>
              <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#ECFDF5", color: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "13px", flexShrink: 0 }}>
                {i + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: "13.5px" }}>{FECHA(a.fecha)} · {METODO_LABEL[a.metodo] || a.metodo}</div>
                <div style={{ fontSize: "12px", color: "#9ca3af" }}>Saldo después: {COLONES(a.saldo_despues)}</div>
              </div>
              <div style={{ fontWeight: 800, color: "#22c55e", fontSize: "14px" }}>+{COLONES(a.monto)}</div>
            </div>
          ))}
          {abonos.length === 0 && (
            <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af", fontSize: "13px" }}>Aún no se han registrado abonos.</div>
          )}
        </div>

        {/* Acciones */}
        <div style={{ display: "flex", gap: "10px" }}>
          <a
            href={`https://wa.me/506${telLimpio}?text=${mensajeWhatsapp}`}
            target="_blank"
            rel="noreferrer"
            style={{ ...btnPrimary, background: "#25D366", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "8px" }}
          >
            💬 Enviar por WhatsApp
          </a>
          <button onClick={descargarPDF} disabled={descargandoPDF} style={btnGhost}>
            {descargandoPDF ? "Generando..." : "📄 Descargar / Enviar PDF"}
          </button>
        </div>
      </div>
    );
  }

  // ---------- Vista de búsqueda ----------
  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: "16px" }}>
        <div style={statLabel}>Buscar cliente</div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Nombre o cédula..."
          style={{ ...inputStyle, width: "100%", maxWidth: "360px" }}
        />
      </div>

      {error && (
        <div style={{ background: "#FEF3C7", color: "#B45309", borderRadius: "10px", padding: "12px 16px", marginBottom: "16px", fontSize: "13px", fontWeight: 700 }}>
          {error}
        </div>
      )}

      {cargandoDetalle && <div style={{ textAlign: "center", padding: "20px", color: "#9ca3af" }}>Cargando estado de cuenta...</div>}
      {cargandoLista && <div style={{ textAlign: "center", padding: "20px", color: "#9ca3af" }}>Buscando...</div>}

      {!cargandoLista && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {resultados.map((c) => {
            const totalPendiente = c.creditos.reduce((s, cr) => s + cr.saldo_pendiente, 0);
            const abierto = clienteAbierto === c.cliente_id;
            return (
              <div key={c.cliente_id} style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
                <button
                  onClick={() => setClienteAbierto(abierto ? null : c.cliente_id)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: "12px", padding: "16px 20px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: "14.5px" }}>{c.nombre}</div>
                    <div style={{ fontSize: "12.5px", color: "#9ca3af" }}>
                      {c.cedula} · {c.creditos.length} {c.creditos.length === 1 ? "crédito" : "créditos"}
                    </div>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: "14px", color: totalPendiente === 0 ? "#22c55e" : "#0D47A1" }}>
                    {totalPendiente === 0 ? "Al día" : COLONES(totalPendiente)}
                  </div>
                  <span style={{ color: "#d1d5db", transform: abierto ? "rotate(90deg)" : undefined, transition: "transform .15s" }}>▸</span>
                </button>

                {abierto && (
                  <div style={{ borderTop: "1px solid #f3f4f6" }}>
                    <div style={{ padding: "12px 20px", background: "#FEF2F2", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap", borderBottom: "1px solid #f3f4f6" }}>
                      <div style={{ fontSize: "12.5px", color: "#6b7280" }}>Comprobante rápido con el saldo total, para enviar por WhatsApp</div>
                      <button onClick={() => descargarComprobanteSaldo(c)} disabled={descargandoComprobante === c.cliente_id} style={{ ...btnPrimary, background: "#DC2626" }}>
                        {descargandoComprobante === c.cliente_id ? "Generando..." : "🧾 Comprobante de saldo (PDF)"}
                      </button>
                    </div>
                    <div style={{ padding: "12px 20px", background: "#F9FAFB", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                      <div style={{ fontSize: "12.5px", color: "#6b7280" }}>Todas sus compras a crédito en una sola hoja</div>
                      <button onClick={() => descargarPDFCliente(c)} disabled={descargandoCliente === c.cliente_id} style={btnPrimary}>
                        {descargandoCliente === c.cliente_id ? "Generando..." : "📄 Estado de cuenta del cliente (PDF)"}
                      </button>
                    </div>
                    {c.creditos.map((cr) => (
                      <button
                        key={cr.venta_credito_id}
                        onClick={() => abrirCredito(cr.venta_credito_id)}
                        style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", background: "transparent", border: "none", borderTop: "1px solid #f9fafb", cursor: "pointer", textAlign: "left" }}
                      >
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "13px" }}>Compra #{cr.venta_credito_id} · {FECHA(cr.fecha)}</div>
                          <div style={{ fontSize: "12px", color: "#9ca3af" }}>Saldo inicial: {COLONES(cr.saldo_inicial)}</div>
                        </div>
                        <span
                          style={{
                            fontSize: "11.5px",
                            fontWeight: 700,
                            padding: "4px 10px",
                            borderRadius: "999px",
                            background: cr.al_dia ? "#ECFDF5" : "#FEF3C7",
                            color: cr.al_dia ? "#065F46" : "#B45309",
                          }}
                        >
                          {cr.al_dia ? "Al día" : "Pendiente"}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {resultados.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px", color: "#9ca3af", background: "#fff", borderRadius: "16px" }}>
              {query ? `No se encontraron clientes con "${query}".` : "No hay clientes con créditos registrados."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
